import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../../assets/styles/GlobalStyles';
import { FaFileAlt, FaCalendarAlt, FaHashtag, FaPlay, FaTrashAlt, FaQrcode, FaRedo } from 'react-icons/fa';
import { Button } from '../../../components/ui/Button/Button';
import { getContrastTextColor } from '../../../utils/colorUtils';
import CodigoCopiavel from '../../requerimentos/core/components/CodigoCopiavel';
import { formatCurrency } from '../../requerimentos/core/services/pagamentoService';
import { isCobrancaVencida, isCobrancaEmAberto } from '../../requerimentos/core/services/tituloService';
import { SUPPORT_CONTACTS } from '../../support/constants/supportContacts';

const Card = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: rgba(0, 112, 243, 0.2);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const RequestTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a2c3e;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: ${colors.darkBlue};
    font-size: 1.2rem;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => props.$bgColor || '#f0f0f0'};
  color: ${props => props.$textColor || '#333'};
  max-width: 100%;
  overflow-wrap: anywhere;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef2f6;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #5a6e7c;

  svg {
    color: ${colors.darkBlue};
    font-size: 0.9rem;
    opacity: 0.7;
  }

  strong {
    color: #1a2c3e;
    font-weight: 600;
    margin-right: 4px;
  }
`;

const Acoes = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eef2f6;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  @media (max-width: 480px) {
    > button {
      flex: 1 1 100%;
      justify-content: center;
    }
  }
`;

const AcoesTexto = styled.p`
  flex: 1 1 240px;
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: #5a6e7c;

  strong {
    color: #1a2c3e;
  }
`;

const ErroAcao = styled.p`
  flex: 1 1 100%;
  margin: 0;
  font-size: 0.82rem;
  color: #c62828;
`;

// Ações de um pedido que ficou pela metade (Pendente, sem protocolo):
// "Continuar" só aparece quando o progresso está salvo neste navegador;
// "Descartar" cancela o requerimento (situacao 9), com confirmação antes.
const AcoesRascunho = ({ request, rascunhoLocal, onContinuar, onDescartar }) => {
  const [confirmando, setConfirmando] = useState(false);
  const [descartando, setDescartando] = useState(false);
  const [erro, setErro] = useState(null);

  const handleConfirmarDescarte = async () => {
    setDescartando(true);
    setErro(null);
    try {
      await onDescartar(request);
    } catch (err) {
      setErro(err.message || 'Não foi possível descartar o pedido. Tente novamente.');
      setDescartando(false);
    }
  };

  if (confirmando) {
    return (
      <Acoes>
        <AcoesTexto>
          Descartar o pedido <strong>Nº {request.solicitacaoServico}</strong>? Ele será cancelado e não poderá ser retomado.
        </AcoesTexto>
        <Button variant="secondary" size="small" onClick={() => setConfirmando(false)} disabled={descartando}>
          Voltar
        </Button>
        <Button variant="danger" size="small" onClick={handleConfirmarDescarte} disabled={descartando} leftIcon={<FaTrashAlt />}>
          {descartando ? 'Descartando...' : 'Sim, descartar'}
        </Button>
        {erro && <ErroAcao role="alert">{erro}</ErroAcao>}
      </Acoes>
    );
  }

  return (
    <Acoes>
      <AcoesTexto>
        {rascunhoLocal
          ? 'Este pedido não foi concluído. Você pode continuar de onde parou.'
          : 'Este pedido não foi concluído e o progresso não está salvo neste navegador. Continue pelo navegador onde começou ou descarte-o e faça um novo.'}
      </AcoesTexto>
      <Button variant="secondary" size="small" onClick={() => setConfirmando(true)} leftIcon={<FaTrashAlt />}>
        Descartar
      </Button>
      {rascunhoLocal && (
        <Button variant="primary" size="small" onClick={() => onContinuar(rascunhoLocal)} leftIcon={<FaPlay />}>
          Continuar pedido
        </Button>
      )}
    </Acoes>
  );
};

const PainelCobranca = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ResumoCobranca = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  font-size: 0.85rem;
  color: #5a6e7c;

  strong {
    color: #1a2c3e;
    margin-right: 4px;
  }
`;

const AvisoCobranca = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.82rem;
  line-height: 1.4;
  background: #fff8e1;
  color: #8a6100;
`;

const CONTATO_OAB = `Entre em contato com o Departamento de Desenvolvimento da OAB/MS pelos telefones ${SUPPORT_CONTACTS.telefones.map((t) => t.number).join(' ou ')}.`;

// "2026-10-05" -> "05/10/2026", sem passar por Date (fuso).
const formatarVencimento = (data) => {
  const [ano, mes, dia] = String(data || '').slice(0, 10).split('-');
  return dia ? `${dia}/${mes}/${ano}` : '—';
};

const ConteudoCobranca = ({ cobranca }) => {
  if (!cobranca) {
    return <AvisoCobranca>Não encontramos a cobrança deste pedido. {CONTATO_OAB}</AvisoCobranca>;
  }

  const resumo = (
    <ResumoCobranca>
      <span><strong>Valor:</strong>{formatCurrency(cobranca.valor)}</span>
      <span><strong>Vencimento:</strong>{formatarVencimento(cobranca.dataVencimento)}</span>
    </ResumoCobranca>
  );

  // O GET só exclui título cancelado: um já pago volta também, e o pedido fica
  // Aguardando Pagamento até o ERP processar — não mostra o PIX de novo.
  if (!isCobrancaEmAberto(cobranca.situacao)) {
    return (
      <>
        {resumo}
        <AvisoCobranca>
          Esta cobrança não está mais em aberto{cobranca.situacaoNome ? ` (situação: ${cobranca.situacaoNome})` : ''}. Se você já fez o pagamento, aguarde a confirmação — o pedido será atualizado automaticamente.
        </AvisoCobranca>
      </>
    );
  }

  // Título existe, mas o registro no Banco do Brasil falhou: sem pagador
  // (endereço vem do formulário do pedido) não dá pra registrar de novo daqui.
  if (!cobranca.linhaDigitavel && !cobranca.pixCopiaECola) {
    return (
      <>
        {resumo}
        <AvisoCobranca>A cobrança deste pedido não foi registrada no banco. {CONTATO_OAB}</AvisoCobranca>
      </>
    );
  }

  // Esconde os códigos pra ninguém tentar pagar um título vencido.
  if (isCobrancaVencida(cobranca.dataVencimento)) {
    return (
      <>
        {resumo}
        <AvisoCobranca>Esta cobrança está vencida. {CONTATO_OAB}</AvisoCobranca>
      </>
    );
  }

  // A API registra boleto e PIX juntos, então os dois servem, independente
  // da forma escolhida no pedido.
  return (
    <>
      {resumo}
      <div>
        <CodigoCopiavel label="PIX Copia e Cola" valor={cobranca.pixCopiaECola} qrCode />
        <CodigoCopiavel label="Linha digitável do boleto" valor={cobranca.linhaDigitavel} linkHref={cobranca.urlBoleto} linkLabel="Abrir boleto em PDF" />
      </div>
    </>
  );
};

// Pedido Aguardando Pagamento: mostra de novo o PIX/linha digitável da taxa,
// buscados na API de Títulos só quando a pessoa abre (não a cada card listado).
const AcoesPagamento = ({ request, onBuscarCobranca }) => {
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [cobranca, setCobranca] = useState(undefined);
  const [erro, setErro] = useState(null);

  const buscar = async () => {
    setCarregando(true);
    setErro(null);
    try {
      setCobranca(await onBuscarCobranca(request));
    } catch (err) {
      setErro(err.message || 'Não foi possível carregar a cobrança. Tente novamente.');
    } finally {
      setCarregando(false);
    }
  };

  const handleAbrir = () => {
    setAberto(true);
    if (cobranca === undefined) buscar();
  };

  if (!aberto) {
    return (
      <Acoes>
        <AcoesTexto>Este pedido aguarda o pagamento da taxa.</AcoesTexto>
        <Button variant="primary" size="small" onClick={handleAbrir} leftIcon={<FaQrcode />}>
          Ver pagamento
        </Button>
      </Acoes>
    );
  }

  return (
    <Acoes>
      <PainelCobranca>
        {carregando && <AcoesTexto>Carregando cobrança...</AcoesTexto>}
        {!carregando && erro && <ErroAcao role="alert">{erro}</ErroAcao>}
        {!carregando && !erro && cobranca !== undefined && <ConteudoCobranca cobranca={cobranca} />}
      </PainelCobranca>
      {!carregando && erro && (
        <Button variant="secondary" size="small" onClick={buscar} leftIcon={<FaRedo />}>
          Tentar novamente
        </Button>
      )}
      <Button variant="secondary" size="small" onClick={() => setAberto(false)} disabled={carregando}>
        Ocultar
      </Button>
    </Acoes>
  );
};

const RequestCard = ({ request, isRascunho = false, isAguardandoPagamento = false, rascunhoLocal = null, onContinuar, onDescartar, onBuscarCobranca }) => {
  const statusColor = request.situacaoCor || '#6c757d';
  const textColor = getContrastTextColor(statusColor);

  return (
    <Card>
      <CardHeader>
        <RequestTitle>
          <FaFileAlt />
          {request.modeloRequerimento}
        </RequestTitle>
        <StatusBadge $bgColor={statusColor} $textColor={textColor}>
          {request.situacao}
        </StatusBadge>
      </CardHeader>

      <InfoGrid>
        <InfoItem>
          <FaHashtag />
          <strong>Nº do Pedido:</strong> {request.solicitacaoServico}
        </InfoItem>
        <InfoItem>
          <FaCalendarAlt />
          <strong>Data de Criação:</strong> {request.dataCriacao}
        </InfoItem>
      </InfoGrid>

      {isRascunho && (
        <AcoesRascunho
          request={request}
          rascunhoLocal={rascunhoLocal}
          onContinuar={onContinuar}
          onDescartar={onDescartar}
        />
      )}

      {isAguardandoPagamento && (
        <AcoesPagamento request={request} onBuscarCobranca={onBuscarCobranca} />
      )}
    </Card>
  );
};

export default RequestCard;